import { useDeferredValue, useMemo, useState } from 'react';
import {
  Download,
  Filter,
  Layers3,
  List,
  Map as MapIcon,
  MapPin,
  RefreshCcw,
  Save,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { PERMISSIONS, hasPermission } from '@helios/authz';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Input,
  Spinner,
} from '@helios/ui';
import { useAuth } from '@/features/auth/authContext';
import { useRealtimeDashboard } from '@/hooks/useRealtimeDashboard';
import { sessionScopeKey, useSessionScopeFence } from '@/hooks/useSessionScope';
import { apiClient } from '@/services/apiClient';
import type { ProjectMapResponse } from '@/services/realtimeClient';
import { COHORT_PRESENTATION } from '../projectMapCohorts';
import { AtlasMap } from './AtlasMap';
import { AtlasLayers } from './AtlasLayers';
import { AtlasInspector, SourceStatus } from './AtlasInspector';
import { AtlasRegister } from './AtlasRegister';
import {
  useLocationIntelligence,
  useResourceLayer,
  type LocationIntelligence,
  type LocationRequest,
} from './atlasApi';
import {
  DEFAULT_LAYERS,
  EMPTY_FILTERS,
  STAGES,
  distanceMiles,
  filterJobs,
  jobPoint,
  jobsCsv,
  jobsFromSnapshot,
  money,
  number,
  stageLabel,
  type AtlasFilters,
  type BaseMap,
  type Job,
  type LayerId,
  type MapPoint,
} from './atlasModel';
import {
  download,
  loadViews,
  locationBrief,
  preset,
  saveViews,
  type SavedAtlasView,
} from './atlasViews';
import './project-atlas.css';

type Comparison = {
  id: string;
  title: string;
  data?: LocationIntelligence;
  radius: number;
  jobs: number;
};
export function ProjectAtlasWorkspace() {
  const scope = useSessionScopeFence(PERMISSIONS.PROJECTS_READ);
  if (!scope.enabled)
    return (
      <EmptyState
        title="Project map access required"
        description="Sign in with project-read access to view this workspace."
      />
    );
  return <AtlasLiveWorkspace key={JSON.stringify(sessionScopeKey(scope.identity))} />;
}
function AtlasLiveWorkspace() {
  const { projectMapQuery, socketStatus } = useRealtimeDashboard();
  if (projectMapQuery.isLoading)
    return (
      <div className="atlas-loading" role="status">
        <Spinner className="h-5 w-5" />
        Loading the complete job register…
      </div>
    );
  if (projectMapQuery.isError || !projectMapQuery.data)
    return (
      <ErrorState
        title="Project map unavailable"
        description="The authorized source could not be loaded. No empty or cached portfolio is substituted."
        action={<Button onClick={() => void projectMapQuery.refetch()}>Retry map</Button>}
      />
    );
  return (
    <AtlasContent
      data={projectMapQuery.data}
      connected={socketStatus === 'connected'}
      refreshing={projectMapQuery.isFetching}
      onRefresh={() => void projectMapQuery.refetch()}
    />
  );
}
/** Exported separately so isolated UI tests exercise the real workspace without a fabricated runtime route. */
export function AtlasContent({
  data,
  connected,
  refreshing,
  onRefresh,
}: {
  data: ProjectMapResponse;
  connected: boolean;
  refreshing: boolean;
  onRefresh: () => void;
}) {
  const auth = useAuth();
  const scope = useSessionScopeFence(PERMISSIONS.PROJECTS_READ);
  const [filters, setFilters] = useState<AtlasFilters>(EMPTY_FILTERS);
  const [layers, setLayers] = useState<LayerId[]>(DEFAULT_LAYERS);
  const [basemap, setBasemap] = useState<BaseMap>('dark');
  const [view, setView] = useState<'map' | 'register'>('map');
  const [radius, setRadius] = useState<1 | 3 | 5>(3);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pointOverride, setPointOverride] = useState<MapPoint | null>(null);
  const [request, setRequest] = useState<LocationRequest | null>(null);
  const [addressMode, setAddressMode] = useState(false);
  const [address, setAddress] = useState('');
  const [extraFilters, setExtraFilters] = useState(false);
  const [showLayers, setShowLayers] = useState(true);
  const [modal, setModal] = useState<'sources' | 'saved' | 'brief' | 'compare' | null>(null);
  const [cluster, setCluster] = useState<Job[]>([]);
  const [comparisons, setComparisons] = useState<Comparison[]>([]);
  const [viewName, setViewName] = useState('');
  const [notice, setNotice] = useState('');
  const [exporting, setExporting] = useState(false);
  const storageKey =
    'helios:project-atlas:views:v1:' + scope.identity.tenantId + ':' + scope.identity.userId;
  const [saved, setSaved] = useState<SavedAtlasView[]>(() => loadViews(storageKey));
  const jobs = useMemo(() => jobsFromSnapshot(data), [data]);
  const job = jobs.find((j) => j.id === selectedId) ?? null;
  const selectedPoint = job ? jobPoint(job) : null;
  const intelligence = useLocationIntelligence(
    selectedId
      ? selectedPoint
        ? { latitude: selectedPoint.latitude, longitude: selectedPoint.longitude }
        : null
      : request,
  );
  // A refreshed record always owns its current coordinates. Address lookups remain separate from job records.
  const point = job ? jobPoint(job) : (pointOverride ?? intelligence.data?.location ?? null);
  const deferredSearch = useDeferredValue(filters.search);
  const filtered = useMemo(
    () => filterJobs(jobs, { ...filters, search: deferredSearch }, point, radius),
    [jobs, filters, deferredSearch, point, radius],
  );
  const nearby = useMemo(
    () =>
      point
        ? filtered.filter((j) => {
            const p = jobPoint(j);
            return p && distanceMiles(point, p) <= radius;
          })
        : [],
    [filtered, point, radius],
  );
  const resources = useResourceLayer(layers.includes('resources'));
  const sectors = useMemo(
    () => [...new Set(jobs.flatMap((j) => (j.sector ? [j.sector] : [])))].sort(),
    [jobs],
  );
  const regions = useMemo(
    () => [...new Set(jobs.flatMap((j) => (j.location.state ? [j.location.state] : [])))].sort(),
    [jobs],
  );
  const years = useMemo(
    () =>
      [
        ...new Set(
          jobs.flatMap((j) => (j.schedule.startDate ? [j.schedule.startDate.slice(0, 4)] : [])),
        ),
      ]
        .sort()
        .reverse(),
    [jobs],
  );
  const clients = useMemo(
    () =>
      [
        ...new Map(
          jobs.flatMap((j) =>
            j.canonicalClientIdentityId
              ? [[j.canonicalClientIdentityId, j.clientName ?? 'Linked client'] as const]
              : [],
          ),
        ).entries(),
      ].sort((a, b) => a[1].localeCompare(b[1])),
    [jobs],
  );
  const unlocated = jobs.filter((j) => !jobPoint(j)).length;
  const complete = data.coverage?.projects === 'complete' && data.coverage.bids === 'complete';
  const canExport = hasPermission(auth.permissions, PERMISSIONS.REPORTS_EXPORT);
  const updateFilter = <K extends keyof AtlasFilters>(key: K, value: AtlasFilters[K]) =>
    setFilters((current) => ({ ...current, [key]: value }));
  const select = (selected: Job) => {
    setSelectedId(selected.id);
    setPointOverride(null);
    setCluster([]);
    const p = jobPoint(selected);
    setRequest(p ? { latitude: p.latitude, longitude: p.longitude } : null);
  };
  const selectPoint = (p: MapPoint) => {
    setSelectedId(null);
    setPointOverride(p);
    setRequest({ latitude: p.latitude, longitude: p.longitude });
  };
  const closeInspector = () => {
    setSelectedId(null);
    setPointOverride(null);
    setRequest(null);
    updateFilter('nearby', false);
  };
  const title =
    job?.name ?? intelligence.data?.location?.label ?? point?.label ?? 'Selected location';
  const brief = locationBrief(title, intelligence.data, radius, nearby.length);
  const exportFile = async (format: 'csv' | 'brief', records = filtered) => {
    if (!scope.isCurrent(scope.identity) || exporting || !canExport) return;
    setExporting(true);
    setNotice('');
    const content = format === 'csv' ? jobsCsv(records) : brief;
    try {
      const result = await apiClient.post<{ authorizedAt: string; permissions: string[] }>(
        '/maps/export-receipt',
        { format, snapshotAt: data.generatedAt },
        { sessionOrigin: scope.identity.sessionOrigin, allowRefresh: false },
      );
      if (!scope.isCurrent(scope.identity)) return;
      if (!result.ok || !result.data)
        throw new Error(result.error ?? 'Export authorization unavailable.');
      if (
        JSON.stringify([...new Set(result.data.permissions)].sort()) !== scope.identity.permissions
      )
        throw new Error('Permissions changed. Refresh before exporting.');
      download(
        content,
        'HELIOS-project-map-' +
          new Date().toISOString().slice(0, 10) +
          (format === 'csv' ? '.csv' : '.md'),
        format === 'csv' ? 'text/csv;charset=utf-8' : 'text/markdown;charset=utf-8',
      );
      setNotice('Export downloaded.');
    } catch (error) {
      if (scope.isCurrent(scope.identity))
        setNotice(error instanceof Error ? error.message : 'Export failed.');
    } finally {
      if (scope.isCurrent(scope.identity)) setExporting(false);
    }
  };
  const addComparison = () => {
    if (!point) return;
    const id = point.latitude + ':' + point.longitude;
    if (comparisons.length >= 3 && !comparisons.some((item) => item.id === id)) {
      setModal('compare');
      return;
    }
    setComparisons((current) =>
      [
        ...current.filter((c) => c.id !== id),
        { id, title, data: intelligence.data, radius, jobs: nearby.length },
      ].slice(-3),
    );
    setModal('compare');
  };
  const persistViews = (values: SavedAtlasView[]) => {
    try {
      saveViews(storageKey, values);
      setSaved(values);
      setNotice('Saved on this device for your workspace.');
      return true;
    } catch {
      setNotice('This browser could not save the view.');
      return false;
    }
  };
  return (
    <div className="project-atlas" aria-label="Project Map workspace">
      <header className="atlas-heading">
        <div>
          <h1>Project Map</h1>
          <p>Portfolio & location intelligence</p>
        </div>
        <div className="atlas-heading-actions">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setNotice('');
              setModal('saved');
            }}
          >
            <Save size={13} />
            Saved views
          </Button>
          {comparisons.length ? (
            <Button size="sm" variant="outline" onClick={() => setModal('compare')}>
              Compare locations ({comparisons.length})
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="outline"
            onClick={() => void exportFile(point ? 'brief' : 'csv')}
            disabled={!canExport || exporting}
            title={!canExport ? 'Report export permission is required' : undefined}
          >
            <Download size={13} />
            {exporting ? 'Exporting…' : 'Export ' + (point ? 'brief' : 'register')}
          </Button>
          <button
            type="button"
            className="atlas-refresh"
            onClick={() => {
              onRefresh();
              if (layers.includes('resources')) void resources.refetch();
            }}
            disabled={refreshing}
            aria-label="Refresh map records"
          >
            <RefreshCcw size={14} />
            <span>
              {connected ? 'Live updates' : 'Snapshot'}
              <small>
                {new Date(data.generatedAt).toLocaleTimeString([], {
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </small>
            </span>
          </button>
        </div>
      </header>
      <nav className="atlas-stage-strip" aria-label="Job stages">
        <button
          type="button"
          aria-pressed={!filters.stages.length}
          onClick={() => updateFilter('stages', [])}
        >
          All jobs{' '}
          <strong>
            {jobs.length}
            {!complete ? '+' : ''}
          </strong>
        </button>
        {STAGES.map((stage) => {
          const summary = data.cohorts?.find((s) => s.cohort === stage);
          const count = summary?.records ?? jobs.filter((j) => j.cohort === stage).length;
          return (
            <button
              type="button"
              key={stage}
              aria-pressed={filters.stages.includes(stage)}
              onClick={() =>
                updateFilter(
                  'stages',
                  filters.stages.includes(stage)
                    ? filters.stages.filter((s) => s !== stage)
                    : [...filters.stages, stage],
                )
              }
            >
              <i style={{ background: COHORT_PRESENTATION[stage].color }} />
              {stageLabel(stage)}{' '}
              <strong>
                {summary?.availability === 'restricted' ? '—' : count}
                {summary?.availability === 'partial' ? '+' : ''}
              </strong>
            </button>
          );
        })}
      </nav>
      <div className="atlas-toolbar">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (addressMode && address.trim().length >= 8) {
              const repeatAddress =
                !selectedId &&
                request &&
                'address' in request &&
                request.address === address.trim();
              setSelectedId(null);
              setPointOverride(null);
              if (repeatAddress) void intelligence.refetch();
              else setRequest({ address: address.trim() });
            }
          }}
        >
          <label className="sr-only" htmlFor="atlas-search-mode">
            Search mode
          </label>
          <select
            id="atlas-search-mode"
            value={addressMode ? 'address' : 'jobs'}
            onChange={(e) => setAddressMode(e.target.value === 'address')}
          >
            <option value="jobs">Jobs</option>
            <option value="address">Address</option>
          </select>
          <Input
            aria-label={addressMode ? 'Full street address' : 'Search jobs, clients or locations'}
            maxLength={addressMode ? 300 : 200}
            value={addressMode ? address : filters.search}
            onChange={(e) =>
              addressMode ? setAddress(e.target.value) : updateFilter('search', e.target.value)
            }
            placeholder={
              addressMode
                ? 'Full US street address, city, state…'
                : 'Search jobs, clients or locations…'
            }
            startAdornment={<Search size={14} />}
          />
          {addressMode ? (
            <Button
              size="sm"
              type="submit"
              disabled={address.trim().length < 8 || intelligence.isFetching}
            >
              Look up
            </Button>
          ) : null}
        </form>
        <select
          aria-label="Project start year"
          value={filters.year}
          onChange={(e) => updateFilter('year', e.target.value)}
        >
          <option value="">All start years</option>
          {years.map((year) => (
            <option key={year}>{year}</option>
          ))}
          <option value="unknown">Start date unrecorded</option>
        </select>
        <select
          aria-label="Sector"
          value={filters.sector}
          onChange={(e) => updateFilter('sector', e.target.value)}
        >
          <option value="">All sectors</option>
          {sectors.map((sector) => (
            <option key={sector}>{sector}</option>
          ))}
        </select>
        <select
          aria-label="Region"
          value={filters.region}
          onChange={(e) => updateFilter('region', e.target.value)}
        >
          <option value="">All regions</option>
          {regions.map((region) => (
            <option key={region}>{region}</option>
          ))}
        </select>
        <button
          type="button"
          aria-label="More filters"
          aria-expanded={extraFilters}
          onClick={() => setExtraFilters(!extraFilters)}
        >
          <SlidersHorizontal size={15} />
        </button>
        <button
          type="button"
          className="atlas-layer-button"
          aria-label="Toggle layers panel"
          aria-pressed={showLayers}
          onClick={() => setShowLayers(!showLayers)}
        >
          <Layers3 size={15} />
        </button>
        <div className="atlas-view-toggle">
          {(['map', 'register'] as const).map((mode) => (
            <button
              type="button"
              key={mode}
              aria-pressed={view === mode}
              onClick={() => setView(mode)}
            >
              {mode === 'map' ? <MapIcon size={14} /> : <List size={14} />}
              {mode}
            </button>
          ))}
        </div>
      </div>
      {extraFilters ? (
        <div className="atlas-filter-drawer">
          <Filter size={14} />
          <select
            aria-label="Client identity"
            value={filters.client}
            onChange={(e) => updateFilter('client', e.target.value)}
          >
            <option value="">All clients</option>
            {clients.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
          <select
            aria-label="Recorded risk"
            value={filters.risk}
            onChange={(e) => updateFilter('risk', e.target.value)}
          >
            <option value="">All risk levels</option>
            {['LOW', 'MODERATE', 'HIGH', 'CRITICAL'].map((r) => (
              <option key={r}>{r}</option>
            ))}
            <option value="unknown">Risk unrecorded</option>
          </select>
          <select
            aria-label="Location coverage"
            value={filters.location}
            onChange={(e) => updateFilter('location', e.target.value as AtlasFilters['location'])}
          >
            <option value="all">All location records</option>
            <option value="located">Located jobs</option>
            <option value="unlocated">Needs coordinates</option>
          </select>
          <label>
            <input
              type="checkbox"
              checked={filters.nearby}
              disabled={!point}
              onChange={(e) => updateFilter('nearby', e.target.checked)}
            />
            Within {radius} miles of selection
          </label>
          <button
            type="button"
            className="atlas-text-button"
            onClick={() => setFilters(EMPTY_FILTERS)}
          >
            Clear filters
          </button>
        </div>
      ) : null}
      {notice ? (
        <div className="atlas-notice-bar" role="status">
          {notice}
          <button type="button" aria-label="Dismiss notice" onClick={() => setNotice('')}>
            <X size={13} />
          </button>
        </div>
      ) : null}
      <div className="atlas-coverage-bar">
        <span>
          {data.tenant?.name ?? 'Current workspace'} · {filtered.length} matching ·{' '}
          {filtered.filter((j) => jobPoint(j)).length} located
        </span>
        <button
          type="button"
          onClick={() => {
            updateFilter('location', filters.location === 'unlocated' ? 'all' : 'unlocated');
            setView('register');
          }}
        >
          {unlocated} need coordinates
        </button>
        {!complete ? (
          <button type="button" onClick={() => setModal('sources')}>
            Partial or restricted source coverage
          </button>
        ) : (
          <span>Complete authorized source query</span>
        )}
        {resources.isFetching ? (
          <span>Loading assignments…</span>
        ) : resources.isError ? (
          <button type="button" onClick={() => void resources.refetch()}>
            Assignments unavailable · retry
          </button>
        ) : resources.data ? (
          <span>
            Crew: {resources.data.crews} · Equipment: {resources.data.equipment}
          </span>
        ) : null}
      </div>
      <main
        className={
          'atlas-workspace ' +
          (!showLayers ? 'atlas-workspace--no-layers' : '') +
          (view === 'register' ? 'atlas-workspace--register' : '')
        }
      >
        {showLayers ? (
          <AtlasLayers
            layers={layers}
            basemap={basemap}
            onBasemap={setBasemap}
            onSources={() => setModal('sources')}
            onLayer={(id) =>
              setLayers((current) =>
                current.includes(id)
                  ? current.filter((item) => item !== id)
                  : [
                      ...current.filter(
                        (item) =>
                          !(id === 'value' && item === 'clients') &&
                          !(id === 'clients' && item === 'value'),
                      ),
                      id,
                    ],
              )
            }
            onPreset={(value) => {
              const next = preset(value);
              setFilters(next.filters);
              setLayers(next.layers);
            }}
          />
        ) : null}
        <div className="atlas-center">
          {view === 'map' ? (
            <div className="atlas-map-wrap">
              <AtlasMap
                jobs={filtered}
                resourceData={resources.data}
                selectedId={selectedId}
                point={point}
                radius={radius}
                layers={layers}
                basemap={basemap}
                onSelect={select}
                onPoint={selectPoint}
                onCluster={setCluster}
              />
              {point ? (
                <div className="atlas-floating-radius">
                  <MapPin size={13} />
                  <span>{radius} mi radius</span>
                  <button
                    type="button"
                    aria-pressed={filters.nearby}
                    onClick={() => updateFilter('nearby', !filters.nearby)}
                  >
                    {filters.nearby ? 'Radius filter on' : 'Filter to area'}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
          <AtlasRegister
            jobs={view === 'map' && point ? nearby : filtered}
            selectedId={selectedId}
            onSelect={select}
            full={view === 'register'}
            nearby={view === 'map' && !!point}
            radius={radius}
            onExport={() => void exportFile('csv', view === 'map' && point ? nearby : filtered)}
            canExport={canExport && !exporting}
          />
        </div>
        <AtlasInspector
          job={job}
          point={point}
          radius={radius}
          onRadius={(value) => setRadius(value as 1 | 3 | 5)}
          query={intelligence}
          nearby={nearby}
          resourceData={resources.data}
          onClose={closeInspector}
          onSources={() => setModal('sources')}
          onBrief={() => setModal('brief')}
          onCompare={addComparison}
          compareCount={comparisons.length}
        />
      </main>
      <Dialog
        open={modal !== null}
        onOpenChange={(open) => {
          if (!open) setModal(null);
        }}
      >
        <DialogContent className="atlas-dialog sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {modal === 'saved'
                ? 'Saved map views'
                : modal === 'sources'
                  ? 'Sources & coverage'
                  : modal === 'compare'
                    ? 'Compare locations'
                    : 'Location brief'}
            </DialogTitle>
            <DialogDescription>
              {modal === 'saved'
                ? 'Filters, layers and appearance saved for your workspace on this device.'
                : modal === 'compare'
                  ? 'Up to three location snapshots. Each figure retains its source geography and period.'
                  : 'Recorded observations, geography, source dates and availability.'}
            </DialogDescription>
          </DialogHeader>
          {modal === 'sources' ? (
            <div className="atlas-dialog-scroll">
              <div className="atlas-source">
                <strong>Job register</strong>
                <p>{data.coverage?.basis ?? 'Authorized native project records.'}</p>
                <p>
                  {jobs.length} measured records · {unlocated} without coordinates · snapshot{' '}
                  {new Date(data.generatedAt).toLocaleString()}
                </p>
              </div>
              {intelligence.data?.sources.map((source) => (
                <SourceStatus key={source.id} source={source} />
              )) ?? (
                <p className="atlas-muted">
                  Select a point or look up an address to check external source availability.
                </p>
              )}
              <div className="atlas-source">
                <strong>Map layers</strong>
                <p>
                  Census boundaries retain their full tract/county geography. FEMA polygons show
                  mapped coverage only. OpenStreetMap supplies community-mapped development, road,
                  supplier and amenity features; results are partial and capped at 250 per query.
                  Parcels require an approved jurisdiction connection.
                </p>
              </div>
              <div className="atlas-source">
                <strong>Historical completeness</strong>
                <p>
                  This register shows records admitted by the connected project and bid sources.
                  Work that has never been imported or recorded cannot appear here. Deleted,
                  portfolio-excluded, accounting-only and unaccepted source records remain outside
                  the map.
                </p>
              </div>
            </div>
          ) : null}
          {modal === 'saved' ? (
            <div className="atlas-dialog-scroll">
              <form
                className="atlas-save-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!viewName.trim() || saved.length >= 20) return;
                  if (
                    persistViews([
                      ...saved,
                      {
                        id: crypto.randomUUID(),
                        name: viewName.trim(),
                        filters: { ...filters, nearby: false },
                        layers,
                        basemap,
                        radius,
                        view,
                      },
                    ])
                  )
                    setViewName('');
                }}
              >
                <Input
                  aria-label="View name"
                  placeholder="Name this view…"
                  maxLength={60}
                  value={viewName}
                  onChange={(e) => setViewName(e.target.value)}
                />
                <Button type="submit" size="sm" disabled={!viewName.trim() || saved.length >= 20}>
                  Save current view
                </Button>
              </form>
              <p className="atlas-provenance">
                Saved views omit selected addresses and job data. Radius filtering resets until a
                location is selected.
              </p>
              {saved.map((item) => (
                <div key={item.id} className="atlas-saved-row">
                  <button
                    type="button"
                    onClick={() => {
                      setFilters(item.filters);
                      setLayers(item.layers);
                      setBasemap(item.basemap);
                      setRadius(item.radius);
                      setView(item.view);
                      setModal(null);
                    }}
                  >
                    {item.name}
                    <small>
                      {item.layers.length} layers · {item.view}
                    </small>
                  </button>
                  <button
                    type="button"
                    aria-label={'Delete saved view ' + item.name}
                    onClick={() => persistViews(saved.filter((v) => v.id !== item.id))}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              {!saved.length ? <p>No saved views yet.</p> : null}
              {notice ? <p role="status">{notice}</p> : null}
            </div>
          ) : null}
          {modal === 'brief' ? (
            <>
              <pre className="atlas-brief atlas-dialog-scroll">{brief}</pre>
              <Button
                size="sm"
                disabled={!canExport || exporting}
                onClick={() => void exportFile('brief')}
              >
                <Download size={14} />
                Download brief
              </Button>
            </>
          ) : null}
          {modal === 'compare' ? (
            <div className="atlas-compare atlas-dialog-scroll">
              <p>
                {comparisons.length >= 3
                  ? 'Remove a location to make room for another comparison.'
                  : 'Close this panel, select another site, then choose Compare to add it.'}
              </p>
              <div>
                {comparisons.map((item) => (
                  <article key={item.id}>
                    <button
                      type="button"
                      aria-label={'Remove ' + item.title + ' from comparison'}
                      onClick={() =>
                        setComparisons((items) => items.filter((c) => c.id !== item.id))
                      }
                    >
                      <X size={13} />
                    </button>
                    <h3>{item.title}</h3>
                    <p className="atlas-provenance">
                      {item.data?.demographics.geography ?? 'Geography unavailable'}
                      <br />
                      {item.data?.demographics.period}
                    </p>
                    <dl>
                      {item.data?.demographics.metrics.slice(0, 4).map((m) => (
                        <div key={m.id}>
                          <dt>{m.label}</dt>
                          <dd>{m.unit.startsWith('USD') ? money(m.value) : number(m.value)}</dd>
                        </div>
                      ))}
                      <div>
                        <dt>Daytime population · county</dt>
                        <dd>{number(item.data?.daytime?.population)}</dd>
                      </div>
                      {item.data?.growth?.metrics.map((m) => (
                        <div key={'growth-' + m.id}>
                          <dt>{m.label} change · county</dt>
                          <dd>{m.percent === null ? 'Unavailable' : m.percent.toFixed(1) + '%'}</dd>
                        </div>
                      ))}
                      <div>
                        <dt>Intersecting parcel records</dt>
                        <dd>
                          {item.data?.parcels?.records.length
                            ? item.data.parcels.records.length
                            : 'Unavailable'}
                        </dd>
                      </div>
                      <div>
                        <dt>Jobs within {item.radius} mi</dt>
                        <dd>{item.jobs}</dd>
                      </div>
                    </dl>
                    <p className="atlas-provenance">
                      {item.data?.daytime?.geography}
                      <br />
                      {item.data?.daytime?.period}
                      <br />
                      {item.data?.growth
                        ? item.data.growth.previousPeriod + ' → ' + item.data.growth.currentPeriod
                        : ''}
                      <br />
                      {item.data?.growth?.basis}
                    </p>
                    <p className="atlas-provenance">
                      Retrieved{' '}
                      {item.data?.retrievedAt
                        ? new Date(item.data.retrievedAt).toLocaleString()
                        : 'No source observations'}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      <Dialog
        open={cluster.length > 0}
        onOpenChange={(open) => {
          if (!open) setCluster([]);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Jobs at this location</DialogTitle>
            <DialogDescription>Choose a source record to inspect.</DialogDescription>
          </DialogHeader>
          <div className="atlas-dialog-scroll">
            {cluster.map((j) => (
              <button
                type="button"
                key={j.id}
                className="atlas-cluster-job"
                onClick={() => select(j)}
              >
                {j.name}
                <span>
                  {stageLabel(j.cohort)} · {j.projectNumber}
                </span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
